import React, { useCallback, useEffect, useMemo, useState } from "react";
import StatusPill from "../components/common/StatusPill";
import {
  AlertCircle, CheckCircle, Clock, DollarSign, Eye, Loader2, Search, TrendingUp, XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import StatStrip from "../components/common/StatStrip";
import PageHeader from "../components/common/PageHeader";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { loadWithdrawals, approveWithdrawal, rejectWithdrawal } from "../services/withdrawalService";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (ms) =>
  ms ? new Date(ms).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS = {
  pending: { label: "Pending", icon: Clock, tone: "amber" },
  approved: { label: "Approved", icon: CheckCircle, tone: "green" },
  rejected: { label: "Rejected", icon: XCircle, tone: "red" },
};
const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return <StatusPill tone={s.tone} icon={s.icon}>{s.label}</StatusPill>;
};

const Row = ({ label, value }) =>
  value ? (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "8px 0", borderBottom: "1px solid hsl(var(--border))", fontSize: 13 }}>
      <span style={{ color: "hsl(var(--muted-foreground))" }}>{label}</span>
      <span style={{ fontWeight: 600, color: "hsl(var(--foreground))", textAlign: "right", wordBreak: "break-all" }}>{value}</span>
    </div>
  ) : null;

const Withdrawals = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null); // details dialog
  const [action, setAction] = useState(null); // { type: "approve" | "reject", item }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setItems(await loadWithdrawals());
    } catch (e) {
      setError(e?.message || "Couldn't load withdrawals");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const stats = useMemo(() => ({
    total: items.length,
    pending: items.filter((w) => w.status === "pending").length,
    approved: items.filter((w) => w.status === "approved").length,
    rejected: items.filter((w) => w.status === "rejected").length,
    totalAmount: items.reduce((s, w) => s + w.amount, 0),
  }), [items]);

  const filtered = items.filter((w) => {
    const q = searchTerm.trim().toLowerCase();
    const hit = !q || [w.id, w.doctorName, w.destination, w.bankAccount, w.upiId].some((v) => String(v || "").toLowerCase().includes(q));
    return hit && (filterStatus === "all" || w.status === filterStatus);
  });

  const confirm = async () => {
    if (!action) return;
    setBusy(true);
    setError("");
    try {
      if (action.type === "approve") await approveWithdrawal(action.item.id);
      else await rejectWithdrawal(action.item.id, reason.trim());
      setAction(null);
      setSelected(null);
      setReason("");
      await refresh();
    } catch (e) {
      setError(e?.message || "Couldn't update the withdrawal");
      setAction(null);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !items.length) return <LoadingSpinner fullHeight message="Loading withdrawals..." />;

  const pill = (color, label, Icon, onClick, disabled) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid hsl(var(--tint-${color}-border))`, background: `hsl(var(--tint-${color}))`, color: `hsl(var(--on-${color}))`, fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}
    >
      <Icon size={12} /> {label}
    </button>
  );

  return (
    <div className="space-y-5">
      <PageHeader title="Withdrawal Management" description="Review, approve or reject payout requests from doctors" className="!mb-2" />

      {error && (
        <div role="alert" style={{ padding: "10px 14px", borderRadius: 10, background: "hsl(var(--tint-red))", border: "1px solid hsl(var(--tint-red-border))", color: "hsl(var(--on-red))", fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
          <AlertCircle size={15} /> {error}
          <button onClick={refresh} style={{ marginLeft: "auto", textDecoration: "underline", fontWeight: 700 }}>Retry</button>
        </div>
      )}

      <StatStrip
        items={[
          { label: "Total requests", value: stats.total, icon: DollarSign, tone: "blue", active: filterStatus === "all", onClick: () => setFilterStatus("all") },
          { label: "Pending", value: stats.pending, icon: Clock, tone: "amber", active: filterStatus === "pending", onClick: () => setFilterStatus("pending") },
          { label: "Approved", value: stats.approved, icon: CheckCircle, tone: "green", active: filterStatus === "approved", onClick: () => setFilterStatus("approved") },
          { label: "Rejected", value: stats.rejected, icon: XCircle, tone: "red", active: filterStatus === "rejected", onClick: () => setFilterStatus("rejected") },
          { label: "Total amount", value: inr(stats.totalAmount), icon: TrendingUp, tone: "violet" },
        ]}
      />

      <div className="admin-card" style={{ padding: "14px 18px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "hsl(var(--muted-foreground))" }} />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by ID, doctor, UPI or account…"
            style={{ width: "100%", paddingLeft: 30, paddingRight: 12, height: 36, borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", boxSizing: "border-box" }}
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger style={{ width: 160, height: 36, borderRadius: 9 }}><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <DollarSign size={40} style={{ color: "hsl(var(--muted-foreground))", opacity: 0.5, margin: "0 auto 12px" }} />
            <p style={{ fontSize: 15, fontWeight: 600, color: "hsl(var(--foreground))" }}>No withdrawals found</p>
            <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
              {items.length ? "Try adjusting your search or filter" : "Doctors' payout requests will appear here"}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                  {["Doctor", "Amount", "Payout to", "Status", "Requested", ""].map((h) => (
                    <th key={h} style={{ padding: "11px 14px", fontSize: 10.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((w) => (
                  <tr key={w.id} onClick={() => setSelected(w)} style={{ cursor: "pointer", borderBottom: "1px solid hsl(var(--border))" }}>
                    <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>{w.doctorName}</td>
                    <td className="num" style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>{inr(w.amount)}</td>
                    <td style={{ padding: "12px 14px", fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>{w.destination || "—"}</td>
                    <td style={{ padding: "12px 14px" }}><StatusBadge status={w.status} /></td>
                    <td className="num" style={{ padding: "12px 14px", fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{fmtDate(w.date)}</td>
                    <td style={{ padding: "12px 14px" }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        {w.status === "pending" && (
                          <>
                            {pill("green", "Approve", CheckCircle, () => setAction({ type: "approve", item: w }))}
                            {pill("red", "Reject", XCircle, () => { setReason(""); setAction({ type: "reject", item: w }); })}
                          </>
                        )}
                        <Button variant="outline" size="icon" className="h-8 w-8" aria-label="View details" onClick={() => setSelected(w)}>
                          <Eye size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details */}
      <Dialog open={!!selected && !action} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent style={{ maxWidth: 460 }}>
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>Withdrawal request</DialogTitle>
                <DialogDescription>{fmtDate(selected.date)}</DialogDescription>
              </DialogHeader>
              <div style={{ textAlign: "center", padding: "6px 0 10px" }}>
                <p className="num" style={{ margin: 0, fontSize: 30, fontWeight: 600, color: "hsl(var(--foreground))" }}>{inr(selected.amount)}</p>
                <div style={{ marginTop: 6 }}><StatusBadge status={selected.status} /></div>
              </div>
              <div>
                <Row label="Doctor" value={selected.doctorName} />
                <Row label="Request ID" value={selected.id} />
                <Row label="Method" value={selected.method} />
                <Row label="UPI ID" value={selected.upiId} />
                <Row label="Bank" value={selected.bankName} />
                <Row label="Account" value={selected.bankAccount} />
                <Row label="IFSC" value={selected.ifsc} />
                <Row label="Processed" value={selected.processedAt ? fmtDate(selected.processedAt) : ""} />
                <Row label={selected.status === "rejected" ? "Rejection reason" : "Note"} value={selected.note} />
              </div>
              {selected.status === "pending" && (
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button variant="outline" onClick={() => { setReason(""); setAction({ type: "reject", item: selected }); }}>Reject</Button>
                  <Button onClick={() => setAction({ type: "approve", item: selected })}>Approve</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve / reject confirmation */}
      <Dialog open={!!action} onOpenChange={(o) => !o && !busy && setAction(null)}>
        <DialogContent style={{ maxWidth: 420 }}>
          {action && (
            <>
              <DialogHeader>
                <DialogTitle>{action.type === "approve" ? "Approve withdrawal?" : "Reject withdrawal?"}</DialogTitle>
                <DialogDescription>
                  {inr(action.item.amount)} for {action.item.doctorName}
                  {action.type === "approve" ? ". Mark it approved once you've paid the doctor." : ". The doctor will see it as rejected."}
                </DialogDescription>
              </DialogHeader>
              {action.type === "reject" && (
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional)"
                  rows={3}
                  style={{ width: "100%", borderRadius: 10, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted))", color: "hsl(var(--foreground))", padding: 10, fontSize: 13, outline: "none", resize: "vertical" }}
                />
              )}
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" disabled={busy} onClick={() => setAction(null)}>Cancel</Button>
                <Button
                  disabled={busy}
                  onClick={confirm}
                  style={action.type === "reject" ? { background: "linear-gradient(180deg, hsl(0 78% 58%), hsl(0 72% 46%))" } : undefined}
                >
                  {busy && <Loader2 size={14} className="animate-spin" />}
                  {action.type === "approve" ? "Approve" : "Reject"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Withdrawals;
